import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { EMPTY, mergeMap } from 'rxjs';

import { DocsActions } from '../docs/docs.actions';
import { selectActiveProjectId } from '../workspace/workspace.selectors';
import { DbDesignActions } from './db-design.actions';
import { compactChatMessages } from './db-design.models';

const DB_KEYS = ['db-design.chat', 'db-design.main', 'db-design.layout', 'define.definition', 'repos.settings'] as const;

export const loadDbDesign$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(DbDesignActions.load),
      concatLatestFrom(() => store.select(selectActiveProjectId)),
      mergeMap(([, pid]) => (pid ? DB_KEYS.map((key) => DocsActions.loadDoc({ projectId: pid, key })) : EMPTY))
    ),
  { functional: true }
);

export const saveSchema$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(DbDesignActions.saveSchema),
      concatLatestFrom(() => store.select(selectActiveProjectId)),
      mergeMap(([{ schema }, pid]) => {
        if (!pid) return EMPTY;
        const data = { schema };
        return [
          DocsActions.applyLocalData({ projectId: pid, key: 'db-design.main', data }),
          DocsActions.saveDoc({ projectId: pid, key: 'db-design.main', data }),
        ];
      })
    ),
  { functional: true }
);

/** Live schema draft: update the cache only (no persist). */
export const setSchemaDraft$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(DbDesignActions.setSchemaDraft),
      concatLatestFrom(() => store.select(selectActiveProjectId)),
      mergeMap(([{ schema }, pid]) =>
        pid ? [DocsActions.applyLocalData({ projectId: pid, key: 'db-design.main', data: { schema } })] : EMPTY
      )
    ),
  { functional: true }
);

export const saveLayout$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(DbDesignActions.saveLayout),
      concatLatestFrom(() => store.select(selectActiveProjectId)),
      mergeMap(([{ layout }, pid]) => {
        if (!pid) return EMPTY;
        const data = layout ?? { positions: {} };
        return [
          DocsActions.applyLocalData({ projectId: pid, key: 'db-design.layout', data }),
          DocsActions.saveDoc({ projectId: pid, key: 'db-design.layout', data }),
        ];
      })
    ),
  { functional: true }
);

/** Live layout draft: update the cache only (no persist). */
const setLayoutDraft$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(DbDesignActions.setLayoutDraft),
      concatLatestFrom(() => store.select(selectActiveProjectId)),
      mergeMap(([{ layout }, pid]) =>
        pid ? [DocsActions.applyLocalData({ projectId: pid, key: 'db-design.layout', data: layout ?? { positions: {} } })] : EMPTY
      )
    ),
  { functional: true }
);

const setChatMessages$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(DbDesignActions.setChatMessages),
      concatLatestFrom(() => store.select(selectActiveProjectId)),
      mergeMap(([{ messages }, pid]) => {
        if (!pid) return EMPTY;
        const data = { messages: compactChatMessages(messages) };
        return [
          DocsActions.applyLocalData({ projectId: pid, key: 'db-design.chat', data }),
          DocsActions.saveDoc({ projectId: pid, key: 'db-design.chat', data }),
        ];
      })
    ),
  { functional: true }
);

export const dbDesignEffects = { loadDbDesign$, saveSchema$, setSchemaDraft$, saveLayout$, setLayoutDraft$, setChatMessages$ };
